"use client";

import { useQuery } from "@tanstack/react-query";
import { isThetaError, type Post } from "@theta/api-client";
import { getPost } from "@/lib/api/posts";
import { livePollOptions } from "./useLivePolling";
import { useIfChanged } from "./useIfChanged";
import { retryUnlessRefused } from "./queryRetry";

/**
 * One post, for the post detail page — `GET /posts/{id}`.
 *
 * `include_comments=false` is gone as a parameter because it is gone as a
 * possibility: v1's `Post` NEVER embeds the thread (the comments are their own
 * paged read, D9), so the endpoint cannot be made to serialise a hundred
 * comments once a second and throw them away, which is what the old flag
 * existed to prevent.
 *
 * A 404 is the answer for a post this viewer may not read and for one that does
 * not exist, deliberately identically (D7) — so it is not retried, and the page
 * renders it as "not found" rather than as a failure.
 *
 * `seed` is the copy the post's own page read on the server, and it is what puts
 * the post in the HTML rather than behind the skeleton. It does NOT save the
 * request: `livePollOptions()` sets `staleTime: 0` — mandatory, for the reasons
 * that file gives — so the browser re-reads on mount. It re-reads behind content
 * that is already on screen, and the 1 Hz poll was going to ask a second later
 * anyway. `null` (a 404, or a failed server read) seeds nothing.
 */
export function useFeedItem(postId: number, seed?: Post | null) {
  const memory = useIfChanged<Post | null>(postId);
  const poll = livePollOptions();

  return useQuery({
    queryKey: postKey(postId),
    queryFn: async () =>
      memory.resolve(await getPost(postId, { ifChanged: memory.token() }), null),
    enabled: postId > 0,
    retry: retryUnlessRefused,
    ...poll,
    /**
     * THE POLL STOPS AT A TERMINAL `notFound` (OSO-675). "Findes ikke" is an
     * answer, and the page renders it as one — a tab parked on it was still
     * asking ~3,600 more times an hour, every request refused, all of them
     * charged to the same viewer-keyed rate bucket the reader's OTHER tabs
     * draw on. Focus and mount refetches still fire (`staleTime: 0`), so a
     * post that comes back is noticed on the next visit; the 1 Hz cadence is
     * for a document that exists.
     */
    refetchInterval: (query) =>
      isThetaError(query.state.error, "notFound") ? false : poll.refetchInterval(),
    ...(seed ? { initialData: seed } : {}),
  });
}

/**
 * The query key a write to this post invalidates, and the one the hook above
 * spells through this function rather than as a literal. The two spellings
 * agreed by coincidence until something wrote to only one of them;
 * `useGroupPage.ts` has the long version of why that failure is silent.
 */
export function postKey(postId: number): (string | number)[] {
  return ["v1", "post", postId];
}
